// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import React, { useState, useEffect, useContext } from 'react'
import { PropTypes } from 'prop-types'
import {
  Box,
  FormField,
  TextInput,
  Text,
  Notification,
  RadioButtonGroup,
  CheckBoxGroup
} from 'grommet'
import { Trans, useTranslation } from 'react-i18next'
import lodashGet from 'lodash/get'
import { useNavigate, useParams } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { WizardContext } from '../../../../../../components/wizard/WizardContext'
import { Anchor, Dropdown, Typography } from '../../../../../../components'
import { get, getErrorMessage } from '../../../../../../utils/api-utils'
import { displayNotification } from '../../../../../../utils/notificiation-utils'
import { isMSP, isV2 } from '../../../../../../utils/feature-flag-utils'
import {
  dispositionEnum,
  ssoModeEnum,
  mspOptions,
  oidcauthorization,
  Authnoptions
} from '../../../constants'

import { validateGeneralFields } from './utils/validation-utils'

const General = ({ onOptionSelect }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { t } = useTranslation(['iam'])
  const LDFlags = useFlags()
  const isGlcpOidc = LDFlags['glcp-oidc']

  const [domains, setDomains] = useState([])
  const { attemptedAdvance, formValues, setFormValues } =
    useContext(WizardContext)
  const [fieldsError, setFieldsError] = useState({
    profileName: '',
    ccsAttribute: '',
    authMethodChangeConfirmation: '',
    mspoption: '',
    disposition: '',
    authnoptions: '',
    domains: ''
  })
  const isDomainSelected =
    lodashGet(formValues, 'domain') &&
    lodashGet(formValues, 'domain') !== 'none'

  const authenticationEditMode = lodashGet(formValues, 'authnEditMode')

  const usersDispositions = [
    {
      label: (
        <div>
          <Trans
            i18nKey="sso_profiles.view_edit_create.least_secure_disposition"
            t={t}
          >
            <strong />
          </Trans>
        </div>
      ),
      value: dispositionEnum.LEAST_SECURE
    },
    {
      label: (
        <div>
          <Trans
            i18nKey="sso_profiles.view_edit_create.most_secure_disposition"
            t={t}
          >
            <strong />
          </Trans>
        </div>
      ),
      value: dispositionEnum.MOST_SECURE
    }
  ]

  const AuthenticationProtocols = [
    {
      label: t('iam:sso_profiles.saml'),
      value: Authnoptions.SAML
    },
    {
      label: t('iam:sso_profiles.openID_connect'),
      value: Authnoptions.OIDC
    }
  ]

  const authorizationMethodsSamlOIDC = [
    {
      label: t('sso_profiles.view_edit_create.local_role_assignments'),
      value: oidcauthorization.LOCAL
    },
    {
      label: t('sso_profiles.view_edit_create.sso_role_assignments'),
      value: oidcauthorization.SSO
    }
  ]
  const authorizationMethodLocalOnly = [
    {
      label: t('sso_profiles.view_edit_create.local_role_assignments'),
      value: oidcauthorization.LOCAL
    }
  ]

  const authorizationMethods = [
    {
      label: t('iam:sso_profiles.view_edit_create.authorization_method_local'),
      value: ssoModeEnum.AUTHENTICATION_ONLY
    },
    {
      label: t('iam:sso_profiles.view_edit_create.authorization_method_saml'),
      value: ssoModeEnum.AUTHORIZATION
    }
  ]

  const SamlOIDCMspMethods = [
    {
      label: t('sso_profiles.view_edit_create.oidv2_msp_option'),
      value: mspOptions.mspOrg
    },
    {
      label: t('sso_profiles.view_edit_create.oidv2_customer_option'),
      value: mspOptions.custOrg
    }
  ]
  // Notifications
  const [notifTitle, setNotifTitle] = useState('')
  const [notifMsg, setNotifMsg] = useState('')
  const [notifSeverity, setNotifSeverity] = useState('')
  const setNotificationInfo = (message, severity, title = '') => {
    setNotifTitle(title)
    setNotifMsg(message)
    setNotifSeverity(severity)
  }

  // The list of domains includes only unclaimed domains. In the edit scenario, we need to include the current domain (which is already claimed) in the list.
  const getDomains = () => {
    const domainList = domains
    if (formValues.currentDomain) {
      const domainExists = domainList.find(
        (item) => item.name === formValues.currentDomain
      )
      if (!domainExists) {
        const domain = {
          id: formValues.currentDomain,
          name: formValues.currentDomain
        }
        domainList.push(domain)
      }
    }
    return domainList
  }

  useEffect(() => {
    let isMounted = true
    const fetchUnclaimedDomains = async () => {
      try {
        const response = await get(
          `/internal-platform-tenant-ui/v2/unclaimed-domains`
        )
        if (isMounted) {
          const items = response?.data?.items
          const domainItems = [
            {
              description: 'adding the domins later',
              name: t('iam:sso_profiles.view_edit_create.none_i_will_add_later')
            },
            ...items
          ]

          setDomains(isGlcpOidc ? domainItems : items)
        }
      } catch (error) {
        if (isMounted) {
          setNotificationInfo(getErrorMessage(error, t), 'error')
          setDomains([])
        }
      }
    }

    fetchUnclaimedDomains()

    return () => {
      isMounted = false
    }
  }, [t, formValues, isGlcpOidc])

  useEffect(() => {
    if (attemptedAdvance) {
      setFieldsError(validateGeneralFields(formValues, t))
    }
  }, [formValues, attemptedAdvance, t])

  const handleAuthenticationProtocolChange = (event) => {
    const selectedValue = event.target.value
    setFormValues({
      ...formValues,
      general: {
        ...formValues.general,
        Authnoptions: selectedValue,
        oidauthorizationMethod: oidcauthorization.LOCAL
      }
    })

    if (onOptionSelect) {
      onOptionSelect(selectedValue)
    }
  }

  let appliesToText
  if (
    formValues.general?.msp_methods?.includes(
      ssoModeEnum.AUTHORIZATION_MSP_MANAGED_TENANT_ONLY
    ) ||
    formValues.general?.msp_methods?.includes(
      ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED_TENANT_ONLY
    )
  ) {
    appliesToText = t(
      'iam:sso_profiles.view_edit_create.customer_organizations'
    )
  } else if (
    formValues.general?.msp_methods?.includes(
      ssoModeEnum.AUTHORIZATION_MSP_MANAGED
    ) ||
    formValues.general?.msp_methods?.includes(
      ssoModeEnum.AUTHENTICATION_ONLY_MSP_MANAGED
    )
  ) {
    appliesToText = t(
      'iam:sso_profiles.view_edit_create.msp_and_customer_organizations'
    )
  } else {
    appliesToText = t('iam:sso_profiles.view_edit_create.msp_organization')
  }

  const optionResetOnDomainNone = (values) => {
    const oidcAuthorizationOptionOnDomain = isDomainSelected
      ? formValues.general.oidauthorizationMethod
      : oidcauthorization.LOCAL

    setFormValues({
      ...formValues,
      general: {
        ...formValues.general,
        oidauthorizationMethod: isGlcpOidc && oidcAuthorizationOptionOnDomain
      },
      domain:
        values?.name ===
        t('iam:sso_profiles.view_edit_create.none_i_will_add_later')
          ? 'none'
          : values.name
    })
  }

  const authorizationMethod = lodashGet(
    formValues,
    'general.authorizationMethod'
  )

  const oidauthorizationMethod = lodashGet(
    formValues,
    'general.oidauthorizationMethod'
  )

  const currentAuthorizationMethod = lodashGet(
    formValues,
    'general.currentAuthorizationMethod'
  )

  const currentOidcAuthorizationMethod = lodashGet(
    formValues,
    'general.currentOidcAuthorizationMethod'
  )

  const showSamlAttribute =
    isDomainSelected &&
    (authorizationMethod === ssoModeEnum.AUTHORIZATION ||
      oidauthorizationMethod === oidcauthorization.SSO)

  const showChangeWarning =
    id &&
    isDomainSelected &&
    formValues.currentDomain &&
    (authorizationMethod !== currentAuthorizationMethod ||
      oidauthorizationMethod !== currentOidcAuthorizationMethod) &&
    showSamlAttribute

  const showChangeConfirmation =
    id &&
    isDomainSelected &&
    formValues.currentDomain &&
    (authorizationMethod !== currentAuthorizationMethod ||
      oidauthorizationMethod !== currentOidcAuthorizationMethod)

  const showDisposition =
    id &&
    isDomainSelected &&
    formValues.currentDomain &&
    (authorizationMethod !== currentAuthorizationMethod ||
      oidauthorizationMethod !== currentOidcAuthorizationMethod) &&
    (authorizationMethod === ssoModeEnum.AUTHENTICATION_ONLY ||
      oidauthorizationMethod === oidcauthorization.LOCAL)

  return (
    <Box gap="medium" pad={{ top: 'xxsmall', bottom: 'medium' }}>
      <Box width="large" gap="medium">
        <Box gap="xsmall">
          <Text size="xsmall" margin={{ top: 'xxsmall' }} weight={500}>
            {t('iam:sso_profiles.view_edit_create.profile_name')}*
          </Text>
          <Text size="xsmall">
            {isGlcpOidc ? (
              <>
                {t(
                  'iam:sso_profiles.view_edit_create.profile_name_description_oidc'
                )}
              </>
            ) : (
              <>
                {t(
                  'iam:sso_profiles.view_edit_create.profile_name_description'
                )}
              </>
            )}
          </Text>
          <FormField
            htmlFor="profile_name"
            name="profile_name"
            error={fieldsError.profileName}
            data-testid="profile-name-form-field"
            required
          >
            <TextInput
              value={lodashGet(formValues, 'general.profileName')}
              id="profile_name"
              name="profile_name_value"
              data-testid="profile-name-input"
              onChange={(e) => {
                setFormValues({
                  ...formValues,
                  general: {
                    ...formValues.general,
                    profileName: e.target.value
                  }
                })
              }}
            />
          </FormField>
          <Notification
            status="info"
            message={
              <Typography type="text" testId="domain-page">
                <Trans
                  i18nKey="iam:sso_profiles.view_edit_create.domain_not_listed"
                  t={t}
                >
                  <Anchor
                    label={t('iam:sso_profiles.view_edit_create.domain_page')}
                    weight={isGlcpOidc ? 500 : 'bold'}
                    onClick={() =>
                      navigate('/manage-account/organization/domains')
                    }
                  />
                </Trans>
              </Typography>
            }
          />
          <Text size="xsmall" margin={{ top: 'xxsmall' }} weight={500}>
            {t('iam:sso_profiles.view_edit_create.domain')}
            {isGlcpOidc && '*'}
          </Text>
          <Text size="xsmall">
            {t('iam:sso_profiles.view_edit_create.domain_description')}
          </Text>
          <FormField
            htmlFor="domain"
            name="domain"
            data-testid="domain-form-field"
            error={isGlcpOidc ? fieldsError.domains : null}
          >
            <Dropdown
              options={getDomains()}
              clear={{
                label: t('iam:sso_profiles.view_edit_create.clear_selection')
              }}
              placeholder={t('iam:sso_profiles.view_edit_create.select')}
              testId="domain-dropdown"
              labelKey="name"
              valueKey="name"
              value={
                formValues.domain === 'none'
                  ? t('iam:sso_profiles.view_edit_create.none_i_will_add_later')
                  : formValues.domain
              }
              onChangeDropdown={(values) => {
                optionResetOnDomainNone(values)
              }}
            />
          </FormField>
          {isGlcpOidc && (
            <>
              {authenticationEditMode ? (
                <>
                  <Text size="xsmall" margin={{ top: 'xsmall' }} weight={500}>
                    {`${t(
                      'iam:sso_profiles.view_edit_create.choose_authentication_method'
                    )}*`}
                  </Text>
                  <FormField
                    data-testid="saml-oidc-edit-authentication-form-field"
                    margin={{ top: 'xsmall' }}
                  >
                    <TextInput value={authenticationEditMode} readOnly />
                  </FormField>
                </>
              ) : (
                <FormField
                  label={`${t(
                    'iam:sso_profiles.view_edit_create.choose_authentication_method'
                  )}*`}
                  data-testid="saml-oidc-authentication-method-form-field"
                  error={fieldsError.authnoptions}
                  margin={{ top: 'xsmall' }}
                >
                  <RadioButtonGroup
                    name="saml-oidc-authentication_protocol"
                    options={AuthenticationProtocols}
                    value={lodashGet(formValues, 'general.Authnoptions')}
                    onChange={handleAuthenticationProtocolChange}
                    data-testid="saml-odic-authentication-method-input"
                  />
                </FormField>
              )}{' '}
            </>
          )}
          {isGlcpOidc && isDomainSelected ? (
            <>
              {isV2() && isMSP() && (
                <>
                  <Text size="xsmall" margin={{ top: 'xxsmall' }} weight={500}>
                    {`${t('iam:sso_profiles.view_edit_create.applies_to')}*`}
                  </Text>
                  <Text size="xsmall">
                    {t(
                      'iam:sso_profiles.view_edit_create.msp_applies_description'
                    )}
                  </Text>
                  {formValues.general.msp_methods ? (
                    <FormField data-testid="saml-oidc-edit-msp-method-form-field">
                      <TextInput value={appliesToText} readOnly />
                    </FormField>
                  ) : (
                    <FormField
                      data-testid="saml-oidc-msp-method-form-field"
                      error={isV2() ? fieldsError.mspoption : null}
                    >
                      <CheckBoxGroup
                        name="msp_methods"
                        options={SamlOIDCMspMethods}
                        data-testid="saml-oidc-msp-method-input"
                      />
                    </FormField>
                  )}
                </>
              )}

              {lodashGet(formValues, 'general.Authnoptions') ===
                Authnoptions.SAML && (
                <FormField
                  label={`${t(
                    'iam:sso_profiles.view_edit_create.choose_authorization_method'
                  )}*`}
                  data-testid="authorizationOidc-method-form-field"
                  margin={{ top: 'xsmall' }}
                >
                  <RadioButtonGroup
                    name="authorization_method"
                    options={authorizationMethodsSamlOIDC}
                    value={lodashGet(
                      formValues,
                      'general.oidauthorizationMethod'
                    )}
                    onChange={(e) => {
                      setFormValues({
                        ...formValues,
                        general: {
                          ...formValues.general,
                          oidauthorizationMethod: e.target.value
                        }
                      })
                    }}
                    data-testid="authorization-method-input"
                  />
                </FormField>
              )}
              {lodashGet(formValues, 'general.Authnoptions') ===
                Authnoptions.OIDC && (
                <FormField
                  label={`${t(
                    'iam:sso_profiles.view_edit_create.choose_authorization_method'
                  )}*`}
                  data-testid="authorization-method-form-field"
                >
                  <RadioButtonGroup
                    name="local_authorization_method"
                    options={authorizationMethodLocalOnly}
                    value={lodashGet(
                      formValues,
                      'general.oidauthorizationMethod'
                    )}
                    onChange={(e) => {
                      setFormValues({
                        ...formValues,
                        general: {
                          ...formValues.general,
                          oidauthorizationMethod: e.target.value
                        }
                      })
                    }}
                    data-testid="local_authorization-method-input"
                  />
                </FormField>
              )}
            </>
          ) : (
            <>
              {!isGlcpOidc && lodashGet(formValues, 'domain') && (
                <>
                  <FormField
                    label={`${t(
                      'iam:sso_profiles.view_edit_create.choose_authorization_method'
                    )}*`}
                    data-testid="authorization-method-form-field"
                  >
                    <RadioButtonGroup
                      name="sso-local-authorization_method"
                      options={authorizationMethods}
                      value={lodashGet(
                        formValues,
                        'general.authorizationMethod'
                      )}
                      onChange={(e) => {
                        setFormValues({
                          ...formValues,
                          general: {
                            ...formValues.general,
                            authorizationMethod: e.target.value
                          }
                        })
                      }}
                      data-testid="authorization-method-input"
                    />
                  </FormField>
                  {isV2() && isMSP() && (
                    <>
                      <Text
                        size="xsmall"
                        margin={{ top: 'xxsmall' }}
                        weight={500}
                      >
                        {`${t(
                          'iam:sso_profiles.view_edit_create.applies_to'
                        )}*`}
                      </Text>
                      <Text size="xsmall">
                        {t(
                          'iam:sso_profiles.view_edit_create.msp_applies_description'
                        )}
                      </Text>
                      {formValues.general.msp_methods ? (
                        <FormField data-testid="saml-oidc-edit-msp-method-form-field">
                          <TextInput value={appliesToText} readOnly />
                        </FormField>
                      ) : (
                        <FormField
                          data-testid="saml-oidc-msp-method-form-field"
                          error={isV2() ? fieldsError.mspoption : null}
                        >
                          <CheckBoxGroup
                            name="msp_methods"
                            options={SamlOIDCMspMethods}
                            data-testid="saml-oidc-msp-method-input"
                          />
                        </FormField>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
          <>
            {showSamlAttribute && (
              <>
                <Text size="xsmall" margin={{ top: 'xxsmall' }} weight={500}>
                  {t('iam:sso_profiles.view_edit_create.saml_attribute_name')}*
                </Text>
                <Text size="xsmall">
                  {t(
                    'iam:sso_profiles.view_edit_create.saml_attribute_description'
                  )}
                </Text>
                <FormField
                  htmlFor="ccs_attribute"
                  name="ccs_attribute"
                  error={fieldsError.ccsAttribute}
                  data-testid="ccs-attribute-form-field"
                  required
                >
                  <TextInput
                    value={lodashGet(
                      formValues,
                      'attribute_mapping.ccsAttribute'
                    )}
                    id="ccs_attribute"
                    name="ccs_attribute_value"
                    data-testid="ccs-attribute-input"
                    onChange={(e) => {
                      setFormValues({
                        ...formValues,
                        attribute_mapping: {
                          ...formValues.attribute_mapping,
                          ccsAttribute: e.target.value
                        }
                      })
                    }}
                  />
                </FormField>
              </>
            )}
            {showChangeWarning && (
              <Notification
                status="info"
                margin={{ top: 'xsmall', bottom: 'xsmall' }}
                message={
                  <Typography type="text" testId="delete-time-warning">
                    <Trans
                      i18nKey="iam:sso_profiles.view_edit_create.local_roles_removal_notice"
                      t={t}
                    />
                  </Typography>
                }
              />
            )}
            {showDisposition && (
              <>
                <Text size="xsmall" margin={{ top: 'xxsmall' }} weight={500}>
                  {t('iam:sso_profiles.view_edit_create.disposition')}*
                </Text>

                <FormField
                  label={`${t(
                    'iam:sso_profiles.view_edit_create.disposition_description'
                  )}`}
                  data-testid="disposition-form-field"
                  error={fieldsError.disposition}
                >
                  <RadioButtonGroup
                    name="disposition"
                    options={usersDispositions}
                    value={lodashGet(formValues, 'general.usersDisposition')}
                    onChange={(e) => {
                      setFormValues({
                        ...formValues,
                        general: {
                          ...formValues.general,
                          usersDisposition: e.target.value
                        }
                      })
                    }}
                    data-testid="disposition-input"
                  />
                </FormField>
              </>
            )}
            {showChangeConfirmation && (
              <>
                <Text size="xsmall" margin={{ top: 'xxsmall' }} weight={500}>
                  {t('iam:sso_profiles.delete.confirmation_title')}*
                </Text>
                <Text size="xsmall">
                  {t(
                    'iam:sso_profiles.view_edit_create.auth_method_update_confirmation_description'
                  )}
                </Text>
                <FormField
                  htmlFor="auth_method_update_confirmation"
                  name="auth_method_update_confirmation"
                  data-testid="delete-confirmation-form-field"
                  required
                  error={fieldsError.authMethodChangeConfirmation}
                >
                  <TextInput
                    padding="xxsmall"
                    value={lodashGet(
                      formValues,
                      'general.authMethodChangeConfirmation'
                    )}
                    id="auth_method_update_confirmation"
                    name="auth_method_update_confirmation_value"
                    data-testid="auth-method-update-confirmation-input"
                    onChange={(e) => {
                      setFormValues({
                        ...formValues,
                        general: {
                          ...formValues?.general,
                          authMethodChangeConfirmation: e.target.value
                        }
                      })
                    }}
                  />
                </FormField>
              </>
            )}
          </>
        </Box>
      </Box>
      {notifMsg &&
        displayNotification(notifMsg, notifSeverity, setNotifMsg, notifTitle)}
    </Box>
  )
}

General.propTypes = {
  onOptionSelect: PropTypes.func.isRequired
}

export default General
